import { Stack } from '@chakra-ui/react';
import { Vehicle } from 'api/types';
import Link from 'components/Link';
import { useUserContext } from 'context/UserContextProvider';
import { isEqual, isNil, keys } from 'lodash';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IdentificationResultIcon } from '../../components/Icons';
import { StatusTag } from '../../components/StatusTag';
import { DataTable } from '../../components/Table/DataTable';
import { SelectColumnFilter } from '../../components/Table/Filter';
import { dateTransformerNoTimezone } from '../../helpers/dateTransformer';
import { createDropdownPlainOptions, statusFilter } from '../../helpers/general';
import useMakesQuery from '../../hooks/useMakesQuery';
import useServiceHistoriesQuery from '../../hooks/useServiceHistoriesQuery';
import { ContentCard } from '../../layout/ContentCard';

const WorkshopServiceHistoryOverviewCard = (props: {
  workshopId: number;
  autoFocus?: boolean;
  setFocusInput?: Dispatch<SetStateAction<boolean>>;
}) => {
  const { t } = useTranslation();
  const { workshopId, autoFocus, setFocusInput } = props;
  const { opUsers, statusCodes } = useUserContext();
  const makesQuery = useMakesQuery();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const serviceHistoryCodes = statusCodes?.service_history_request || {};
  const initialStatus = keys(serviceHistoryCodes).filter((code) => !['0'].includes(code));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialFilters = {
    searchString: '',
    itemsPerPage: 10,
    status: initialStatus || [],
    assignee: undefined,
    make: undefined,
  };
  const localStorageData = window.localStorage.getItem('workshop_service_history_requests_filters');
  // @ts-ignore
  const destringifiedData = JSON.parse(localStorageData) || {};
  useEffect(() => {
    if (!localStorageData) {
      window.localStorage.setItem('workshop_service_history_requests_filters', JSON.stringify(initialFilters));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [searchString, setSearchString] = useState(destringifiedData?.searchString || initialFilters?.searchString);
  const [page, setPage] = useState(1);
  const [make, setMake] = useState(destringifiedData?.make || initialFilters?.make);

  const [itemsPerPage, setItemsPerPage] = useState(destringifiedData?.itemsPerPage || initialFilters?.itemsPerPage);
  const [status, setStatus] = useState(destringifiedData?.status || initialFilters?.status);
  const [assignee, setAssignee] = useState(destringifiedData?.assignee || initialFilters?.assignee);

  const serviceHistoriesQuery = useServiceHistoriesQuery({
    searchString,
    page,
    itemsPerPage,
    status,
    assignee,
    workshopId,
  });

  useEffect(() => {
    setStatus(initialStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusCodes]);

  const initialLoad = searchString === '' && itemsPerPage === 10 && page === 1 && isEqual(initialStatus, status) && isNil(assignee);

  useEffect(() => {
    setPage(1);
  }, [searchString, itemsPerPage]);

  useEffect(() => {
    window.localStorage.setItem(
      'workshop_service_history_requests_filters',
      JSON.stringify({
        searchString: searchString,
        status: status,
        assignee: assignee,
        make: make,
        itemsPerPage: itemsPerPage,
      }),
    );
  }, [searchString, status, assignee, make, itemsPerPage]);

  const allStatusSelected = status.length === Object.keys(serviceHistoryCodes).length;

  const columns = useMemo(
    () => [
      {
        Header: t('forms:id.label'),
        accessor: 'user_readable_id',
        //@ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          return (
            <Link data-test-id={'shr-' + row?.index} to={`/app/op/service-history-request/${row.original.id}`}>
              {row.original.user_readable_id}
            </Link>
          );
        },
      },
      {
        Header: t('forms:status.label'),
        accessor: 'status',
        shouldFilter: true,
        Filter: () => statusFilter(status, setStatus, 'service_history_request', serviceHistoryCodes, allStatusSelected),
        // @ts-ignore
        Cell: ({ value }) => <StatusTag resourceKey={'service_history_request'} currentStatus={value} />,
      },
      {
        Header: t('forms:assignee.label'),
        accessor: 'open_task.assignee.name',
        // @ts-ignore
        Cell: (props) => {
          return props.value ?? '-';
        },
        shouldFilter: true,
        // @ts-ignore
        Filter: ({ column }) =>
          SelectColumnFilter({
            filterValue: assignee,
            column,
            dropdownOptions: createDropdownPlainOptions({
              // @ts-ignore
              records: opUsers,
              valueColumn: 'id',
              labelColumn: 'name',
            }),
            onChange: setAssignee,
          }),
      },
      {
        Header: t('forms:submitted_at.label', { date: '' }),
        accessor: (row: { submitted_at: string }) => dateTransformerNoTimezone(row.submitted_at) ?? '-',
      },
      {
        Header: t('forms:vin.label'),
        accessor: (row: { vehicle: Vehicle }) => row.vehicle?.vin,
      },
      {
        Header: t('forms:make.label'),
        accessor: (row: { vehicle: Vehicle }) => row.vehicle?.manufacturer?.make?.name,
        shouldFilter: true,
        // @ts-ignore
        Filter: ({ column }) =>
          SelectColumnFilter({
            filterValue: make,
            column,
            dropdownOptions: createDropdownPlainOptions({
              // @ts-ignore
              records: makesQuery.data,
              labelColumn: 'name',
              valueColumn: 'id',
            }),
            onChange: setMake,
          }),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [serviceHistoryCodes, initialStatus, allStatusSelected, searchString, status, make, assignee, itemsPerPage, page],
  );

  return (
    <ContentCard
      isLoading={serviceHistoriesQuery.isLoading || makesQuery.isLoading}
      icon={<IdentificationResultIcon boxSize={10} />}
      minH="fit-content"
      header={t('service_history_request:header')}
      data-test-id="service-history-overview-card"
    >
      <Stack direction={['column', 'row']} w={'100%'}>
        <Stack direction="column" w={'100%'}>
          <DataTable
            resetFilter={() => {
              setSearchString(initialFilters?.searchString);
              setPage(1);
              setMake(initialFilters?.make);
              setStatus(initialFilters?.status);
              setAssignee(initialFilters?.assignee);
              setItemsPerPage(initialFilters?.itemsPerPage);
              window.localStorage.setItem('workshop_service_history_requests_filters', JSON.stringify(initialFilters));
            }}
            data-test-id="workshop-service-history-table"
            // @ts-ignore
            data={serviceHistoriesQuery.data?.data}
            globalFilter={searchString}
            // @ts-ignore
            setGlobalFilter={setSearchString}
            autoFocus={autoFocus}
            setFocusInput={setFocusInput}
            globalFilterPlaceholder={t('service_history_request:search')}
            hasFilters={true}
            // @ts-ignore
            currentPage={serviceHistoriesQuery.data?.meta?.current_page - 1}
            perPage={itemsPerPage}
            total={serviceHistoriesQuery.data?.meta?.last_page}
            columns={columns}
            setPage={setPage}
            setPerPage={setItemsPerPage}
            loading={serviceHistoriesQuery.isFetching}
            // @ts-ignore
            filtersInitiallyOpen
            isMoreThenOnePage={
              // @ts-ignore
              !initialLoad || Boolean(serviceHistoriesQuery.data?.next_page_url)
            }
          />
        </Stack>
      </Stack>
    </ContentCard>
  );
};

export default WorkshopServiceHistoryOverviewCard;
